import React from 'react';
import PropTypes from 'prop-types';
import { RadarChart } from '@mantine/charts';
import NumberFormat from 'react-number-format';
import AppText from '../../common/AppText';
import AppStack from '../../common/AppStack';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import { abbreviateString } from '../../../helpers/evaluationPlayerHelper';

const PlayerStatsCardSummary = ({
  player,
  primaryColor,
  secondaryColor,
  primaryTextColor,
  secondaryTextColor,
  pageIndex,
  maxPerPage
}) => {
  const index = pageIndex ?? 1;
  const maxStatsPerPage = maxPerPage ?? 5;

  const playerEvaluationRatings =
    player?.regScoutReportPlayerAnswers.map((a) => ({
      ...a,
      abbr: abbreviateString(a.questionLabel, true)
    })) || [];

  return (
    <AppCard
      shadow="sm"
      style={{
        padding: 0,
        width: '100%',
        border: `solid 2px ${primaryColor}`,
        position: 'relative'
      }}
    >
      <AppStack
        style={{
          backgroundColor: secondaryColor,
          opacity: 0.15,
          width: '100%',
          height: '100%',
          padding: 10,
          position: 'absolute',
          zIndex: 1
        }}
      />
      <AppStack
        style={{
          flex: 1,
          zIndex: 2,
          gap: 0
        }}
      >
        <AppFlexbox style={{ padding: 10 }}>
          <AppText style={{ fontSize: 36, lineHeight: '36px' }} weight={500}>
            {Math.round(player.overallScore * 10) / 10}
          </AppText>
        </AppFlexbox>
        <RadarChart
          data={playerEvaluationRatings
            .map((r) => ({
              label: r.questionLabel,
              value: r.rating
            }))
            .slice(
              -maxStatsPerPage + index * maxStatsPerPage,
              index * maxStatsPerPage
            )}
          dataKey="label"
          h={200}
          polarRadiusAxisProps={{ domain: [1, 5] }}
          radarChartProps={{}}
          series={[
            {
              name: 'value',
              strokeColor: primaryColor,
              color: primaryColor,
              opacity: 0.2
            }
          ]}
          ticks={[1, 2, 3, 4, 5]}
          width={300}
          withPolarAngleAxis
          withPolarGrid
          withPolarRadiusAxis
        />

        <AppFlexbox
          style={{
            padding: '5px 10px',
            justifyContent: 'center',
            textAlign: 'center',
            backgroundColor: primaryColor,
            color: primaryTextColor
          }}
        >
          <AppText style={{ fontSize: 24 }} weight={500}>
            {player.person.firstName} {player.person.lastName}
          </AppText>
        </AppFlexbox>

        <AppFlexbox style={{ padding: 10, justifyContent: 'center' }}>
          {playerEvaluationRatings
            .slice(
              -maxStatsPerPage + index * maxStatsPerPage,
              index * maxStatsPerPage
            )
            .map((r) => (
              <AppStack
                key={r.questionLabel}
                style={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 0
                }}
              >
                <AppText
                  style={{ fontSize: 20, lineHeight: '24px' }}
                  weight={500}
                >
                  {r.abbr}
                </AppText>

                <AppText
                  style={{ fontSize: 20, lineHeight: '24px' }}
                  weight={500}
                >
                  <NumberFormat
                    decimalScale={1}
                    displayType="text"
                    fixedDecimalScale
                    thousandSeparator
                    value={r.rating}
                  />
                </AppText>
              </AppStack>
            ))}
        </AppFlexbox>
      </AppStack>
    </AppCard>
  );
};

PlayerStatsCardSummary.propTypes = {
  maxPerPage: PropTypes.number,
  pageIndex: PropTypes.number,
  player: PropTypes.object,
  primaryColor: PropTypes.string,
  primaryTextColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  secondaryTextColor: PropTypes.string
};

export default PlayerStatsCardSummary;
